(function () {
  'use strict';

  angular
      .module('tfc.Nav')
      .provider('Nav', NavProvider);

  // @ngInject
  function NavProvider() {

    var title = { text : '' };
    var welcome = {
      // @ngInject
      getText : function (TextUtil, Auth) {
        return TextUtil.translateOr('app.welcome', Auth.getUser(), function (user) {
          return 'Welcome, ' + user.firstName;
        });
      }
    };

    var mainMenuItems = [],
        rightMenuItems = [],
        userMenuItems = [
          {
            text : 'Change Password',
            state : 'passwordReset'
          }
        ];

    /**
     * @name NavProvider#addMain
     * Add a nav button that will be left-justified in the navbar.
     *
     * The 'Injectable' type is something that `$injector` understands:
     * @see https://code.angularjs.org/1.4.10/docs/api/auto/service/$injector#annotate
     * Note: The 'Argument names' syntax from the above link is not allowed.
     *
     * The configuration's main methods are:
     * - getText {Injectable} Returns the nav button's text
     * - onClick {Injectable} Function called when the user clicks the nav button
     * - isActive {Injectable} Returns whether the nav button should be highlighted as 'active'
     *
     * The configuration also supports a few short-hand properties, which obviate the need to define
     * the main methods listed above. These short-hand properties are:
     * - text {String} For when the nav button has static text. `getText` is ignored if this is set.
     * - translate {String|Object} For use with angular-translate. This is ignored if `text` is set.
     *        `getText` is ignored if this is set. Setting to a string is the same as { key: `translate` }.
     *        This defines what's passed to the translate filter. If `translate` is an object, it can have:
     *        - key {String|Injectable} Defines the translation key.
     *        - values {Object|Injectable} Defines the translation params.
     *        @see https://angular-translate.github.io/docs/#/api/pascalprecht.translate.filter:translate
     * - state {String|Object} Defines the ui-router state to navigate to onClick.
     *        `onClick` is ignored if this is set. Setting to a string is the same as { name: `state` }.
     *        This defines what's passed to `$state.go`. This will create default `onClick` and `isActive` options,
     *        which you can still overwrite. If `state` is an object, it can have:
     *        - name {String|Injectable} Defines the state to go to.
     *        - params {Object|Injectable} Defines the state params.
     *        - options {Object|Injectable} Defines the `$state.go` options.
     *        @see http://angular-ui.github.io/ui-router/site/#/api/ui.router.state.$state#methods_go
     *
     * @param config {Object} The item's configuration. See above for details
     * @returns {NavProvider} Returns `this`, so as to be chainable
     */
    this.addMain = function (config) {
      require(_.isPlainObject(config), 'You must supply a config object to NavProvider#addMain');
      mainMenuItems.push(config);
      return this;
    };

    /**
     * @name NavProvider#addRight
     * Add a nav button that will be right-justified in the navbar. This has an identical API to NavProvider#addMain
     * @param config {Object} The item's configuration.
     * @returns {NavProvider} Returns `this`, so as to be chainable
     * @see NavProvider#addMain
     */
    this.addRight = function (config) {
      require(_.isPlainObject(config), 'You must supply a config object to NavProvider#addRight');
      rightMenuItems.push(config);
      return this;
    };

    this.addUser = function (config) {
      require(_.isPlainObject(config), 'You must supply a config object to NavProvider#addRight');
      userMenuItems.push(config);
      return this;
    };

    this.setWelcome = function (config) {
      welcome = config;
      return this;
    };

    this.setAppTitle = function (config) {
      title = config;
      return this;
    };

    this.removeChangePassword = function() {
      _.remove(userMenuItems, {state: 'passwordReset'});
      return this;
    };

    this.$get = function ($injector, $sce) {
      var invoke = $injector.invoke;
      /**
       * Returns whether something is injectable.
       * @see https://code.angularjs.org/1.4.10/docs/api/auto/service/$injector#annotate
       * Note: The 'Argument names' syntax from the above link is not allowed.
       */
      function isInjectable(value) {
        try {
          $injector.annotate(value, /*strictDi=*/true);
          return true;
        } catch (e) {
          return false;
        }
      }

      /**
       * Returns a value that can be passed to $injector.invoke. This means
       * if `value` is injectable, `value` is returned. Otherwise, this returns
       * an injectable function that will return `value`.
       */
      function toInjectable(value) {
        return isInjectable(value) ? value : function () {
          return value;
        };
      }

      function configureState(config) {
        if (config.state) {
          // support simple `state: 'foo'` syntax
          if (_.isString(config.state)) {
            config.state = {
              name : config.state
            };
          }
          // if the values aren't injectable functions, wrap them in one
          config.state.name = toInjectable(config.state.name);
          config.state.params = toInjectable(config.state.params);
          config.state.options = toInjectable(config.state.options);
          // _.defaults to allow overriding
          _.defaults(config, {
            // @ngInject
            onClick : function ($state) {
              $state.go(
                  invoke(config.state.name),
                  invoke(config.state.params),
                  invoke(config.state.options)
              );
            },
            // @ngInject
            isActive : function ($state) {
              return $state.includes(invoke(config.state.name));
            },
            // Whether the user can access this item
            // @ngInject
            canAccess : function ($state) {
              return $state.can(invoke(config.state.name));
            }
          });
        } else if (!config.onClick && !config.isActive) {
          if (!_.get(config, 'children.length')) {
            console.warn('Nav item configured with no "state" property and no children. ' +
                'Did you forget to add a "state" to this nav item?', config);
          } else {
            // With no state defined, make the parent act as the first child the user has access to
            var fromFirstChild = function(keyPath) {
              return function () {
                var firstAccessibleChild = _.find(config.children, function (child) {
                  return invoke(child.canAccess);
                });
                var fn = _.get(firstAccessibleChild, keyPath);
                return fn && invoke(fn);
              };
            };
            config.state = {
              name : fromFirstChild('state.name'),
              params : fromFirstChild('state.params'),
              options : fromFirstChild('state.options')
            };
            config.onClick = fromFirstChild('onClick');
            config.isActive = fromFirstChild('isActive');
            config.canAccess = fromFirstChild('canAccess');
          }
        }
      }

      function configureText(config) {
        if (_.isString(config.text)) {
          config.getText = function () {
            return config.text;
          };
        } else if (config.translate) {
          // support simple `translate: 'app.welcome'` syntax
          if (_.isString(config.translate)) {
            config.translate = {
              key : config.translate
            };
          }
          config.translate.key = toInjectable(config.translate.key);
          config.translate.values = toInjectable(config.translate.values);
          // @ngInject
          config.getText = function () {
            var translateFilter;
            if (!$injector.has('translateFilter')) {
              throw new Error('Nav: menu item set "translate" property without angular-translate present');
            }
            translateFilter = $injector.get('translateFilter');
            return translateFilter(
                invoke(config.translate.key),
                invoke(config.translate.values)
            );
          };
        }
      }

      function configureTemplate(config) {
        if (!_.isString(config.template)) {
          return;
        }
        config.template = $sce.trustAsHtml(config.template);
      }

      configureText(title);
      configureText(welcome);

      // massage config items so they're usable
      mainMenuItems
          .concat(rightMenuItems)
          .concat(userMenuItems)
          .forEach(function (config) {
            configureState(config);
            configureText(config);
            // TODO: implement the UI for child nav items
            config.children = _.map(config.children, function (child) {
              configureState(child);
              configureText(child);
              // only children get the ability to template for now
              configureTemplate(child);
              return child;
            });
          });

      return {
        appTitle : appTitle,
        appWelcome : appWelcome,
        mainMenu : mainMenu,
        rightMenu : rightMenu,
        userMenu : userMenu
      };

      function appTitle() {
        return title;
      }

      function appWelcome() {
        return welcome;
      }

      function mainMenu() {
        return mainMenuItems
      }

      function rightMenu() {
        return rightMenuItems;
      }

      function userMenu() {
        return userMenuItems;
      }
    };
  }

  function require(x, msg) {
    if (!x) {
      throw new Error(msg);
    }
  }

})();
